import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCategory } from '../api/CasinoApi';
import { Filter, Game, GameMachineData } from '../apiTypes/GameService';
import useLiveData from '../hooks/useLiveData';
import { appendParameterAutomatically, getParameter, removeParameterAutomatically } from '@SharedScripts/Utils/Url';
import { deepCopyArray } from '@SharedScripts/Utils/List';
import { FavoriteSessionObject } from '../Types';
import { FavoriteSessionKey } from '../Constants';
import { initiateLogin } from '@SharedScripts/UserData/Authentication';
import { gameDataAdapter } from '../Components/Tiles/gameDataAdapter';
import useIsLoggedIn from '@SharedScripts/Hooks/useIsLoggedIn';
import useFavorite from './useFavorite';

export interface FilterProps extends Filter {
  type: 'radio' | 'filterChip' | 'checkbox' | 'toggle' | 'volatilitySelector',
}

export interface SelectedFilterProps {
  selected: string[]
}

export const filterTypeMap = {
  Provider: 'checkbox',
  MinimumWager: 'filterChip',
  PlayType: 'checkbox',
  JackpotType: 'checkbox',
  Themes: 'checkbox',
  Bonus: 'toggle',
  Jackpot: 'toggle',
  Volatility: 'volatilitySelector',
  DanishDealer: 'toggle',
  TableStatus: 'toggle'
} as const;

const useFilter = (categoryId: string) => {
  const [isFiltersAvailable, setIsFiltersAvailable] = useState(true);
  const [filters, setFilters] = useState<FilterProps[]>([]);
  const [allSelectedAsList, setAllSelectedAsList] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<Record<keyof typeof filterTypeMap, SelectedFilterProps>>({
    Provider: {
      selected: []
    },
    DanishDealer: {
      selected: []
    },
    Jackpot: {
      selected: []
    },
    Bonus: {
      selected: []
    },
    JackpotType: {
      selected: []
    },
    MinimumWager: {
      selected: []
    },
    PlayType: {
      selected: []
    },
    TableStatus: {
      selected: []
    },
    Themes: {
      selected: []
    },
    Volatility: {
      selected: []
    }
  });

  const [matchingGames, setMatchingGames] = useState<Game[]>([]);
  const [totalFiltersSelected, setTotalFiltersSelected] = useState(0);
  const [hasReadFiltersFromUrl, setHasReadFiltersFromUrl] = useState(false);

  const { isPending, isError, data, error } = useQuery({
    queryKey: ['category', categoryId],
    queryFn: () => getCategory(categoryId),
  });

  const isLoggedIn = useIsLoggedIn();
  const liveData = useLiveData(data?.sections?.at(0)?.games || data?.heroSpot || []);
  const { addFavorite, removeFavorite, removeSuperFavorite, addSuperFavorite, favorites, highlightsIds } = useFavorite();

  const removeSelectedFilters = () => {
    const selectedFilterCopy: Record<keyof typeof filterTypeMap, SelectedFilterProps> = JSON.parse(JSON.stringify(selectedFilters));
    for (const value of Object.values(selectedFilterCopy)) {
      value.selected = [];
    }

    setSelectedFilters(selectedFilterCopy);
  };

  useEffect(() => {
    if (!isPending && (!data || !data.filters)) {
      setIsFiltersAvailable(false);
    }
  }, [isPending]);

  useEffect(() => {
    if (!data || !data.filters) {
      return;
    }

    const filtersFromUrl = JSON.parse(getParameter('casino-filter') || '{}');
    setHasReadFiltersFromUrl(true);

    let selectedFiltersObj: Record<string, SelectedFilterProps> = {};
    const filtersList: FilterProps[] = [];

    for (const filter of data.filters) {
      if (!filter.name) continue;

      selectedFiltersObj[filter.name] = {
        selected: []
      };

      if (filter.name in filterTypeMap) {
        filtersList.push({
          type: filterTypeMap[filter.name as keyof typeof filterTypeMap],
          values: filter.values,
          name: filter.name,
          title: filter.title
        });
      }
    }

    selectedFiltersObj = {
      ...selectedFiltersObj,
      ...filtersFromUrl
    };

    setSelectedFilters(selectedFiltersObj);
    setFilters(filtersList);
  }, [data]);

  useEffect(() => {
    const section = deepCopyArray(data?.sections?.at(0)?.games || []);
    const heroSpot = deepCopyArray(data?.heroSpot || []);

    let gamesMatching = section.length ? section : heroSpot;

    if (!gamesMatching.length) return;

    for (const [filter, value] of Object.entries(selectedFilters)) {
      if (!value.selected.length) continue;
      let key = lowerCaseFirst(filter);
      if (key === 'minimumWager') key = 'minWager';
      if (key === 'bonus') key = 'bonusBuy';
      if (key === 'themes') key = 'theme';
      if (key === 'volatility' && value.selected[0] === 'None') continue;

      gamesMatching = gamesMatching.filter((game) => {
        if (key === 'jackpot') {
          return Boolean(game.machineData?.jackpotType);
        }

        if (key === 'danishDealer' && value.selected.length) {
          return liveData.find((liveData) => liveData?.id === game.id)?.tableData?.dealer?.nationality?.toLowerCase() === 'dk' || false;
        }

        if (key === 'tableStatus' && value.selected.length) {
          return liveData.find((liveData) => liveData?.id === game.id)?.tableData?.open || false;
        }

        if (key === 'minWager') {
          return Number(game.minWager) <= Number(value.selected[0]);
        }

        if (key === 'theme' && game.machineData) {
          return value.selected.some((selected) => game?.machineData?.theme?.includes(selected) || false);
        }

        if (key === 'bonusBuy') {
          return game.bonusBuy;
        }

        if (key in game) {
          return value.selected.includes(game[key]);
        }

        if (game.machineData && key in (game.machineData as GameMachineData)) {
          return value.selected.includes(game.machineData[key]);
        }
      });
    }

    setMatchingGames(gamesMatching);
  }, [selectedFilters]);

  useEffect(() => {
    const allSelected = Object.values(selectedFilters).reduce((acc, current) => {
      return [...acc, ...current.selected];
    }, [] as string[]);

    setAllSelectedAsList(allSelected);
    setTotalFiltersSelected(allSelected.length);

    if (hasReadFiltersFromUrl) {
      if (allSelected.length) {
        appendParameterAutomatically('casino-filter', JSON.stringify(selectedFilters));
      } else {
        removeParameterAutomatically('casino-filter');
      }
    }
  }, [selectedFilters, favorites]);

  // NB! This is a copy of 'dataAdapter.ts' getLobbyGameData(). This is a temporary optimization fix
  // If editing this logic, remember to also change the original logic
  const games = useMemo(() => matchingGames.map((game) => {
    const gameId = game.id ?? '';
    const isFavorite = favorites.includes(gameId);
    const isSuperFavorite = highlightsIds.includes(gameId);
    const isSuperFavoriteSelection = false;
    const favouriteCallback = isSuperFavoriteSelection
      ? (gameId: string) => {
        if (gameId) {
          if (isLoggedIn) {
            addSuperFavorite(gameId);
            document.dispatchEvent(new CustomEvent('casino:search:close'));
          } else {
            const sessionData: FavoriteSessionObject = { type: 'superFavorite', id: gameId };
            sessionStorage.setItem(FavoriteSessionKey, JSON.stringify(sessionData));
            initiateLogin();
            return;
          }
        } else {
          document.dispatchEvent(new CustomEvent('casino:search:open', { detail: { context: 'superFavorite' } }));
        }
      }
      : isSuperFavorite
        ? (gameId: string) => removeSuperFavorite(gameId)
        : isFavorite
          ? (gameId: string) => removeFavorite(gameId)
          : (gameId: string) => {
            if (isLoggedIn) {
              addFavorite(gameId);
            } else {
              const sessionData: FavoriteSessionObject = { type: 'normal', id: gameId };
              sessionStorage.setItem(FavoriteSessionKey, JSON.stringify(sessionData));
              initiateLogin();
            }
          };

    return gameDataAdapter({
      elementId: null,
      gameData: game,
      liveData: liveData?.find(
        (gameLiveData) => gameLiveData?.id === game.id
      ),
      categoryLiveData: undefined,
      favorited: isFavorite,
      isLoading: isPending,
      primaryButtonCallback: () => favouriteCallback(gameId),
      primaryButtonText: undefined,
      secondaryButtonHref: '',
      secondaryButtonText: undefined,
      buttonEnabled: false,
      primaryButtonHref: ''
    });
  }), [matchingGames, favorites, isLoggedIn, liveData]);

  return {
    isPending,
    isError,
    error,
    filters,
    selectedFilters,
    allSelectedAsList,
    setSelectedFilters,
    removeSelectedFilters,
    matchingGames: games,
    categoryName: data?.title ?? '',
    totalFiltersSelected,
    isFiltersAvailable
  };
};

export type useFilterReturn = ReturnType<typeof useFilter>;
export default useFilter;

const lowerCaseFirst = (stringToConvert: string) => {
  return stringToConvert[0].toLowerCase() + stringToConvert.slice(1);
};

